const MAX_SAFE_LENGTH = 90;

const TECHNICAL_PATTERN = /stack(?:trace)?|sqlstate|sequelize|prisma|mongoose|econnrefused|enotfound|etimedout|errno\b|typeerror|referenceerror|syntaxerror|cannot read prop|unexpected token|internal server|failed to fetch|networkerror|network request failed|axios|node_modules|\bat\s+\w+\s+\(|<\/?[a-z]|\{\s*"|status code|request failed|\.js:\d+|duplicate key|constraint|violation:|sql\b|query failed|enoent|eacces|unhandled|undefined is not|null is not|err_/i;

export const HTTP_ERROR_MESSAGES = {
  400: "Please check the details and try again.",
  401: "Your session has expired. Please log in again.",
  403: "You don't have permission to do this.",
  404: "The requested item was not found.",
  409: "This action conflicts with the current status.",
  422: "Please check the details and try again.",
  429: "Too many requests. Please try again later.",
  500: "Something went wrong. Please try again.",
  502: "Something went wrong. Please try again.",
  503: "Something went wrong. Please try again.",
};

export const SUCCESS_MESSAGES = {
  SAVE_DRAFT: "Draft saved.",
  CREATE_CUSTOMER: "Customer added.",
  UPDATE_CUSTOMER: "Customer updated.",
  SUBMIT_BOOKING: "Booking submitted.",
  UPLOAD_DOCUMENT: "Document uploaded.",
  SUBMIT_PAYMENT: "Payment proof submitted.",
  PAY_FROM_WALLET: "Wallet payment applied to the booking.",
  UPDATE_PROFILE: "Profile updated.",
  ADD_PASSENGER: "Passenger added.",
  REMOVE_PASSENGER: "Passenger removed.",
  IMPORT_PASSENGERS: "Passengers imported.",
  CREATE_USER: "Agency user created.",
  UPDATE_USER: "Agency user updated.",
  CANCEL_BOOKING: "Booking cancelled.",
  REQUEST_AMENDMENT: "Amendment request submitted.",
  REQUEST_CANCELLATION: "Cancellation request submitted.",
  WITHDRAW_REQUEST: "Request withdrawn.",
  CREATE_SUPPORT_TICKET: "Support ticket created.",
  ADD_TICKET_COMMENT: "Comment added.",
  UPLOAD_TICKET_ATTACHMENT: "Attachment uploaded.",
  DOWNLOAD_DOCUMENT: "Document downloaded.",
  OPEN_DOCUMENT: "Document opened.",
  DOWNLOAD_RECEIPT: "Receipt downloaded.",
  DOWNLOAD_VISA: "Visa downloaded.",
  DOWNLOAD_KYC: "Document downloaded.",
  REQUEST_REFUND: "Refund request submitted.",
  MARK_NOTIFICATION_READ: "Notification marked as read.",
  MARK_ALL_NOTIFICATIONS_READ: "All notifications marked as read.",
  SAVE_NOTIFICATION_PREFERENCES: "Notification settings saved.",
  EXPORT_REPORT: "Report exported.",
  UPDATE_PASSWORD: "Password updated.",
  LOGIN: "Signed in.",
  LOGOUT: "Signed out.",
  SEND_OTP: "OTP sent.",
  RESEND_OTP: "OTP resent.",
  VERIFY_OTP: "Email verified.",
  REGISTER: "Registration submitted.",
  VERIFY_EMAIL: "Email verified.",
  RESEND_VERIFICATION: "Verification email sent.",
  FORGOT_PASSWORD: "OTP sent to your email.",
  RESET_PASSWORD: "Password reset successfully.",
  SUBMIT_ONBOARDING: "Onboarding submitted.",
  ONBOARDING_VERIFIED: "Onboarding complete.",
  UPLOAD_KYC: "Document uploaded.",
  REPLACE_KYC: "Document submitted.",
};

export const ERROR_MESSAGES = {
  BAD_REQUEST: HTTP_ERROR_MESSAGES[400],
  SESSION_EXPIRED: HTTP_ERROR_MESSAGES[401],
  FORBIDDEN: HTTP_ERROR_MESSAGES[403],
  AGENCY_NOT_VERIFIED: "Complete KYC verification before using bookings and reports.",
  AGENCY_NOT_APPROVED: "This agency is waiting for admin approval.",
  NOT_FOUND: HTTP_ERROR_MESSAGES[404],
  CONFLICT: HTTP_ERROR_MESSAGES[409],
  RATE_LIMIT: HTTP_ERROR_MESSAGES[429],
  SERVER: HTTP_ERROR_MESSAGES[500],
  NETWORK: "Unable to connect. Please try again.",
  GENERIC: "Something went wrong. Please try again.",
  INVALID_CREDENTIALS: "Invalid email or password.",
  LOGIN_FAILED: "Unable to sign in.",
  LOGIN_TOKEN_MISSING: "Unable to sign in.",
  REQUIRED_FIELDS: "Please complete the required fields.",
  REQUIRED_ONBOARDING: "Please complete the required onboarding details.",
  REQUIRED_REGISTRATION: "Please complete the required fields.",
  VERIFY_EMAIL_FIRST: "Verify your email OTP before submitting.",
  OTP_INVALID: "Invalid or expired OTP.",
  OTP_REQUIRED: "Enter the 6-digit OTP.",
  SEND_OTP: "Unable to send OTP.",
  RESEND_OTP: "Unable to resend OTP.",
  VERIFY_OTP: "Unable to verify OTP.",
  FORGOT_PASSWORD: "Unable to send the password reset OTP.",
  REGISTER: "Unable to submit registration.",
  LOAD_COUNTRIES: "Unable to load countries.",
  LOAD_STATES: "Unable to load states.",
  LOAD_CITIES: "Unable to load cities.",
  LOAD_DASHBOARD: "Unable to load dashboard.",
  LOAD_CUSTOMERS: "Unable to load customers.",
  SEARCH_CUSTOMERS: "Unable to search customers.",
  LOAD_CUSTOMER: "Unable to load customer.",
  SAVE_CUSTOMER: "Unable to save customer.",
  CREATE_CUSTOMER: "Unable to add customer.",
  CUSTOMER_LIST_REFRESH: "Customer added, but the list could not be refreshed.",
  CUSTOMER_EMAIL_EXISTS: "This email is already registered.",
  CUSTOMER_MOBILE_EXISTS: "This mobile number is already registered.",
  CUSTOMER_PAN_INVALID: "Enter a valid PAN number (for example ABCDE1234F).",
  CUSTOMER_PAN_FILE: "Upload a PDF, JPG, or PNG PAN card photo up to 10 MB.",
  LOAD_BOOKINGS: "Unable to load bookings.",
  LOAD_BOOKING: "Unable to load booking.",
  LOAD_BOOKING_FORM: "Unable to load booking form.",
  SAVE_DRAFT: "Unable to save draft.",
  CREATE_BOOKING: "Unable to create booking.",
  PACKAGE_SOLD_OUT: "This package is sold out.",
  EXCEED_PACKAGE_SEATS: "Requested pilgrims exceed the available package seats.",
  SUBMIT_BOOKING: "Unable to submit booking.",
  LOAD_PASSENGERS: "Unable to load passengers.",
  ADD_PASSENGER: "Unable to add passenger.",
  REMOVE_PASSENGER: "Unable to remove passenger.",
  IMPORT_PASSENGERS: "Unable to import passengers.",
  DOWNLOAD_PASSENGER_TEMPLATE: "Unable to download the passenger template.",
  LOAD_USERS: "Unable to load agency users.",
  CREATE_USER: "Unable to create the agency user.",
  UPDATE_USER: "Unable to update the agency user.",
  PASSENGER_REQUIRED: "First name, last name, and passport details are required.",
  PASSENGER_FIRST_NAME: "First name is required.",
  PASSENGER_LAST_NAME: "Last name is required.",
  PASSENGER_PASSPORT: "Passport number is required.",
  PASSENGER_PASSPORT_EXPIRY: "Passport expiry is required.",
  PASSENGER_PASSPORT_VALIDITY: "Passport must be valid for at least 6 months.",
  PASSENGER_BOOKING_REQUIRED: "Select a booking first.",
  PASSENGER_PACKAGE_MISSING: "This booking cannot accept passengers yet.",
  PASSENGER_LIMIT: "All passengers for this booking have already been added.",
  PASSENGER_CSV_EMPTY: "Choose a CSV or Excel file first.",
  PASSENGER_CSV_HEADERS: "The file must include the required passenger columns.",
  PASSENGER_CSV_INVALID: "Some rows could not be imported. Valid rows were saved where possible.",
  PASSENGER_CSV_EXCEL: "Upload an XLSX or CSV file.",
  PASSENGER_CSV_LIMIT: "This file has more passengers than the booking allows.",
  CANCEL_BOOKING: "Unable to cancel booking.",
  LOAD_DOCUMENTS: "Unable to load documents.",
  UPLOAD_DOCUMENT: "Unable to upload document.",
  PREVIEW_DOCUMENT: "Unable to preview document.",
  DOWNLOAD_DOCUMENT: "Unable to download document.",
  OPEN_DOCUMENT: "Unable to view document.",
  DOCUMENT_REQUIRED: "Select a document type and file first.",
  DOCUMENT_TYPE: "Use a PDF, JPG, or PNG file.",
  DOCUMENT_TOO_LARGE: "File must be 10 MB or smaller.",
  PASSENGER_PHOTO_TYPE: "Passenger photo must be a JPG or PNG file.",
  PASSENGER_PHOTO_TOO_LARGE: "Passenger photo must be 5 MB or smaller.",
  REQUEST_REASON: "Enter a reason of at least 8 characters.",
  REQUEST_CHANGES: "Describe the requested changes.",
  REQUEST_AMENDMENT: "Unable to submit amendment request.",
  REQUEST_CANCELLATION: "Unable to submit cancellation request.",
  LOAD_BOOKING_REQUESTS: "Unable to load booking requests.",
  CREATE_SUPPORT_TICKET: "Unable to create the support ticket.",
  LOAD_SUPPORT_TICKETS: "Unable to load support tickets.",
  LOAD_SUPPORT_TICKET: "Unable to load the support ticket.",
  ADD_TICKET_COMMENT: "Unable to add the comment.",
  UPLOAD_TICKET_ATTACHMENT: "Unable to upload the attachment.",
  TICKET_REQUIRED: "Subject and description are required.",
  TICKET_COMMENT_REQUIRED: "Enter a comment first.",
  TICKET_CLOSED: "This ticket is closed and can only be viewed.",
  NO_DOCUMENTS: "No documents uploaded yet.",
  DOCUMENT_REFRESH: "Document uploaded, but the list could not be refreshed.",
  LOAD_VISAS: "Unable to load visas.",
  LOAD_VISA: "Unable to load visa details.",
  DOWNLOAD_VISA: "Unable to download visa.",
  LOAD_PAYMENTS: "Unable to load payments.",
  SUBMIT_PAYMENT: "Unable to submit payment proof.",
  PAY_FROM_WALLET: "Unable to pay from wallet.",
  PAYMENT_PROOF_REQUIRED: "Booking, amount, and proof file are required.",
  WALLET_PAYMENT_REQUIRED: "Select a booking and enter an amount within the wallet balance.",
  WALLET_BALANCE_LOW: "Wallet balance is not enough for this amount.",
  PAYMENT_CLOSED_BOOKING: "This booking is cancelled or refunded, so a new payment proof cannot be submitted.",
  PAYMENT_ALREADY_PAID: "This booking is already paid, so a new payment proof cannot be submitted.",
  PAYMENT_PARTIAL_AMOUNT: "Enter a partial amount greater than 0 and less than the outstanding package total.",
  DOWNLOAD_RECEIPT: "Unable to download receipt.",
  REQUEST_REFUND: "Unable to request a refund.",
  LOAD_WALLET: "Unable to load outstanding balances.",
  LOAD_PACKAGES: "Unable to load packages.",
  LOAD_PACKAGE: "Unable to load package details.",
  LOAD_HOTELS: "Unable to load hotels.",
  LOAD_HOTEL: "Unable to load hotel details.",
  LOAD_FLIGHTS: "Unable to load flights.",
  LOAD_FLIGHT: "Unable to load flight details.",
  LOAD_TRANSPORT: "Unable to load transportation.",
  LOAD_TRANSPORT_DETAIL: "Unable to load transportation details.",
  LOAD_REPORT: "Unable to load report.",
  EXPORT_REPORT: "Unable to export report.",
  LOAD_NOTIFICATIONS: "Unable to load notifications.",
  MARK_NOTIFICATION_READ: "Unable to mark notification as read.",
  MARK_ALL_NOTIFICATIONS_READ: "Unable to mark notifications as read.",
  SAVE_NOTIFICATION_PREFERENCES: "Unable to save notification settings.",
  LOAD_PROFILE: "Unable to load profile.",
  UPDATE_PROFILE: "Unable to update profile.",
  UPDATE_PASSWORD: "Unable to change password.",
  RESET_PASSWORD: "Unable to reset password.",
  VERIFY_EMAIL: "Unable to verify email.",
  RESEND_VERIFICATION: "Unable to resend verification email.",
  LOAD_ONBOARDING: "Unable to load onboarding.",
  SUBMIT_ONBOARDING: "Unable to submit onboarding.",
  CHECK_APPROVAL: "Unable to check approval status.",
  CHECK_VERIFICATION: "Unable to check verification status.",
  LOAD_AGENCY: "Unable to load agency details.",
  LOAD_VOUCHER: "Unable to load voucher.",
  UPLOAD_KYC: "Unable to upload document.",
  REPLACE_KYC: "Unable to submit the replacement document.",
  PREVIEW_KYC: "Unable to preview document.",
  DOWNLOAD_KYC: "Unable to download document.",
  KYC_PENDING_REPLACE: "A replacement for this document is already pending.",
  KYC_FILE_MISSING: "This file is missing and cannot be opened.",
  KYC_FILE_REQUIRED: "Choose a PDF, JPG, or PNG file first.",
  KYC_FILE_TYPE: "Only PDF, JPG, and PNG files are allowed.",
  KYC_FILE_SIZE: "File must be 10 MB or smaller.",
  SESSION_VERIFY: "Unable to verify your session. Please try again.",
  CANCEL_UPLOAD: "Upload cancelled.",
};

export const PAGE_COPY = {
  DRAFT_BOOKING: "Add passengers, then submit when you are ready. This is not a confirmed booking.",
};

function normalizeText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function sentenceCount(text) {
  return text.split(/[.!?]+/).filter((part) => part.trim()).length;
}

export function isUserSafeMessage(value) {
  const text = normalizeText(value);
  if (!text) return false;
  if (text.length > MAX_SAFE_LENGTH) return false;
  if (sentenceCount(text) > 1) return false;
  if (TECHNICAL_PATTERN.test(text)) return false;
  if (/^[A-Z0-9_]+$/.test(text) && text.includes("_")) return false;
  return true;
}

export function getSuccessMessage(action) {
  if (SUCCESS_MESSAGES[action]) return SUCCESS_MESSAGES[action];
  return isUserSafeMessage(action) ? normalizeText(action) : "";
}

export function getErrorMessage(action) {
  return ERROR_MESSAGES[action] || ERROR_MESSAGES.GENERIC;
}

export function getPageCopy(key) {
  return PAGE_COPY[key] || "";
}

export function extractRawApiMessage(error) {
  if (typeof error === "string") {
    return normalizeText(error);
  }

  const payload = error?.payload;
  if (payload && typeof payload === "object") {
    const payloadMessage = normalizeText(payload.message);
    if (payloadMessage) return payloadMessage;

    const firstError = payload.errors?.[0];
    const nested = normalizeText(
      firstError?.message || (typeof firstError === "string" ? firstError : "")
    );
    if (nested) return nested;

    const payloadError = normalizeText(payload.error);
    if (payloadError && !/^(error|failed|validationerror)$/i.test(payloadError)) {
      return payloadError;
    }
  }

  return normalizeText(error?.rawMessage || error?.message);
}

export function resolveNoticeMessage(notice) {
  const text = normalizeText(notice);
  if (!text) return "";
  if (SUCCESS_MESSAGES[text]) return SUCCESS_MESSAGES[text];
  if (/^draft saved/i.test(text)) return SUCCESS_MESSAGES.SAVE_DRAFT;
  if (/booking (request )?submitted/i.test(text)) return SUCCESS_MESSAGES.SUBMIT_BOOKING;
  return isUserSafeMessage(text) ? text : "";
}

function statusMessage(status) {
  if (!status) return "";
  if (HTTP_ERROR_MESSAGES[status]) return HTTP_ERROR_MESSAGES[status];
  if (status >= 500) return ERROR_MESSAGES.SERVER;
  return "";
}

function fallbackFrom(fallback) {
  if (!fallback) return ERROR_MESSAGES.GENERIC;
  if (ERROR_MESSAGES[fallback]) return ERROR_MESSAGES[fallback];
  if (isUserSafeMessage(fallback)) return normalizeText(fallback);
  return ERROR_MESSAGES.GENERIC;
}

export function getApiErrorMessage(error, fallback) {
  if (typeof error === "string") {
    return isUserSafeMessage(error) ? normalizeText(error) : fallbackFrom(fallback);
  }

  if (error?.isNetworkError) {
    return ERROR_MESSAGES.NETWORK;
  }

  const raw = extractRawApiMessage(error);
  if (/failed to fetch|networkerror|network request failed|load failed/i.test(raw)) {
    return ERROR_MESSAGES.NETWORK;
  }

  const status = Number(error?.status) || 0;

  if (status === 401) {
    const credentialFallback = fallback === "LOGIN_FAILED" || fallback === "INVALID_CREDENTIALS";
    if (error?.sessionExpired || (!credentialFallback && error?.sessionExpired !== false)) {
      return ERROR_MESSAGES.SESSION_EXPIRED;
    }
    if (isUserSafeMessage(raw)) {
      return raw;
    }
    return fallbackFrom(credentialFallback ? fallback : "INVALID_CREDENTIALS");
  }

  if (status === 403) {
    const code = String(error?.payload?.code || "").toUpperCase();
    if (code === "AGENCY_NOT_VERIFIED") return ERROR_MESSAGES.AGENCY_NOT_VERIFIED;
    if (code === "AGENCY_NOT_APPROVED") return ERROR_MESSAGES.AGENCY_NOT_APPROVED;
    if (isUserSafeMessage(raw) && !/^forbidden$/i.test(raw)) {
      return raw;
    }
    return ERROR_MESSAGES.FORBIDDEN;
  }
  if (status === 404) return ERROR_MESSAGES.NOT_FOUND;
  if (status === 429) return ERROR_MESSAGES.RATE_LIMIT;
  if (status >= 500) return ERROR_MESSAGES.SERVER;

  if (status === 409) {
    return isUserSafeMessage(raw) ? raw : fallbackFrom(fallback === "CONFLICT" ? fallback : "CONFLICT");
  }

  if (status === 400 || status === 422) {
    return isUserSafeMessage(raw) ? raw : fallbackFrom(fallback || "BAD_REQUEST");
  }

  if (isUserSafeMessage(raw)) {
    return raw;
  }

  return fallbackFrom(fallback);
}
